import type React from 'react';
import classNames from 'classnames';

import { PDF_KEEP_TOGETHER_ATTR } from '@/utils/canvasToPdf';

import styles from './GridLayout.module.scss';

export type GridContentLayout =
    | '1'
    | '2'
    /** Like `2`, but drops to one column when the grid itself is too narrow for two boxes. */
    | '2-fluid'
    | '3'
    /** Like `3`, but the column count follows the grid's own width, not the viewport's. */
    | '3-fluid'
    | '4'
    | '2-1'
    | '1-2'
    | '3-1'
    | 'auto-fit'
    | 'auto-fill'
    | '1-fixed'
    /**
     * Like `1-fixed`, but stacks when the grid itself is too narrow rather than at a viewport
     * width. Sized for a 320px second column (one `ContentBlock`).
     */
    | '1-fixed-fluid';

interface GridLayoutProps {
    contentLayout?: GridContentLayout;
    secondColumnWidth?: number | string;
    gap?: 'none' | 'small' | 'default' | 'large';
    rowGap?: 'none' | 'small' | 'default' | 'large';
    columnGap?: 'none' | 'small' | 'default' | 'large';
    alignItems?: 'start' | 'center' | 'end' | 'stretch';
    justifyItems?: 'start' | 'center' | 'end' | 'stretch';
    responsive?: boolean;
    /** In a PDF export, starts a new page rather than cutting through a cell. */
    keepCellsTogetherInPdf?: boolean;
    className?: string;
    children: React.ReactNode;
}

export default function GridLayout(props: GridLayoutProps) {
    const {
        contentLayout = '1',
        secondColumnWidth = '360px',
        gap = 'default',
        rowGap,
        columnGap,
        alignItems,
        justifyItems,
        responsive = true,
        keepCellsTogetherInPdf = false,
        className,
        children,
    } = props;

    const secondColumnWidthValue =
        typeof secondColumnWidth === 'number' ? `${secondColumnWidth}px` : secondColumnWidth;

    const grid = (
        <div
            className={classNames(
                styles.gridLayout,
                styles[`layout-${contentLayout}`],
                styles[`gap-${gap}`],
                rowGap && styles[`row-gap-${rowGap}`],
                columnGap && styles[`column-gap-${columnGap}`],
                alignItems && styles[`align-${alignItems}`],
                justifyItems && styles[`justify-${justifyItems}`],
                { [styles.responsive]: responsive },
                className
            )}
            style={
                contentLayout === '1-fixed' || contentLayout === '1-fixed-fluid'
                    ? ({ '--second-column-width': secondColumnWidthValue } as React.CSSProperties)
                    : undefined
            }
            {...(keepCellsTogetherInPdf && { [PDF_KEEP_TOGETHER_ATTR]: 'children' })}>
            {children}
        </div>
    );

    // The fluid layouts are sized by a container query, and a container cannot query itself —
    // they need a parent to measure. Every other layout renders exactly one element.
    if (
        contentLayout === '3-fluid' ||
        contentLayout === '2-fluid' ||
        contentLayout === '1-fixed-fluid'
    ) {
        return <div className={styles.gridContainer}>{grid}</div>;
    }

    return grid;
}
